import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField
} from '@mui/material';
import {
  useSetActivationIdMutation, useSetLicensePasswordMutation, type License
} from '@store/api/licenseEndpoints';
import { serverMessage } from '@store/error';
import { useEffect, useState, type FormEvent } from 'react';

interface SetCredentialsDialogProps {
  license?: License;
  onClose: () => void;
}

/**
 * License files carry no credentials at all — the administrator picks both the activation
 * id and the password here and hands them to the users. Until both exist the license
 * hands out no seats.
 *
 * Each half has its own endpoint, and only what actually changed is sent: rotating a
 * password should not risk rewriting an activation id that clients already use.
 */
const SetCredentialsDialog = ({ license, onClose }: SetCredentialsDialogProps) => {
  const [setActivationId, { isLoading: isSavingId }] = useSetActivationIdMutation();
  const [setLicensePassword, { isLoading: isSavingPassword }] = useSetLicensePasswordMutation();

  const [activationId, setActivationIdValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setActivationIdValue(license?.id ?? '');
    setPassword('');
    setError(undefined);
  }, [license]);

  const idChanged = !!license && activationId.trim() !== (license.id ?? '');
  const canSave = !!license
    && (idChanged || password.length >= 6)
    && (!!license.id || activationId.trim().length >= 5)
    && (license.passwordSet || password.length >= 6);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!license) return;

    setError(undefined);

    try {
      if (idChanged) {
        await setActivationId({ licenseId: license.licenseId, id: activationId.trim() }).unwrap();
      }
      if (password) {
        await setLicensePassword({ licenseId: license.licenseId, password }).unwrap();
      }

      onClose();
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The credentials could not be saved.');
    }
  };

  return (
    <Dialog open={!!license} onClose={onClose} fullWidth maxWidth='xs'>
      <form onSubmit={onSubmit}>
        <DialogTitle>{license?.id ? 'Change credentials' : 'Set credentials'}</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Clients sign in with this pair. Changing the activation id means every client of
            this license needs the new one; seats already taken keep working until they end.
          </DialogContentText>

          <Stack spacing={2}>
            <TextField
              label='Activation id'
              value={activationId}
              onChange={(event) => setActivationIdValue(event.target.value)}
              helperText='At least 5 characters, no spaces. For example, acme.production'
              autoFocus={!license?.id}
              required
              fullWidth
            />

            <TextField
              label={license?.passwordSet ? 'New password' : 'Password'}
              type='password'
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              helperText={license?.passwordSet
                ? 'Leave empty to keep the current password.'
                : 'At least 6 characters.'}
              autoFocus={!!license?.id}
              fullWidth
            />
          </Stack>

          {error && <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='submit' variant='contained' disabled={!canSave || isSavingId || isSavingPassword}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SetCredentialsDialog;
